import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { Search, ChevronDown, DollarSign, Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllCurrencies, getCurrencyById, detectUserLocation } from "../../redux/Actions/AuthAction";
import { getCurrencySymbol, getCachedCurrencyPreference, setCachedCurrencyPreference, findCurrencyIdByCode } from "../../lib/currencyUtils";

interface Currency {
    id: string;
    name: string;
    code: string;
}

interface CurrencyDropdownProps {
    selectedCurrency?: Currency | null;
    onCurrencySelect: (currency: Currency | null) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    autoDetect?: boolean; // New prop to enable auto-detection
    placement?: "auto" | "top" | "bottom"; // New prop to control dropdown placement
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = memo(({
    selectedCurrency,
    onCurrencySelect,
    placeholder = "Select a currency...",
    className = "",
    disabled = false,
    autoDetect = false,
    placement = "bottom",
}) => {
    const dispatch = useAppDispatch();
    const { currencies, loading } = useAppSelector((state: any) => state.auth || {
        currencies: [],
        loading: false,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);
    const [internalSelectedCurrency, setInternalSelectedCurrency] = useState<Currency | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<"top" | "bottom">("bottom");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasLoadedCurrenciesRef = useRef(false);
    const hasDetectedCurrencyRef = useRef(false);

    // Load currencies on component mount
    useEffect(() => {
        console.log("=== Currency loading useEffect ===");
        console.log("hasLoadedCurrenciesRef.current:", hasLoadedCurrenciesRef.current);
        console.log("loading:", loading);
        
        if (!hasLoadedCurrenciesRef.current && !loading) {
            console.log("📥 Dispatching getAllCurrencies with limit=10...");
            hasLoadedCurrenciesRef.current = true;
            dispatch(getAllCurrencies({
                page: 1,
                limit: 10,  // Load only 10 initially
                search: "",
                isShowLoader: false,
            }));
        }
    }, [dispatch, loading]);

    // Currency detection and caching (only if autoDetect is enabled)
    useEffect(() => {
        const detectAndSelectCurrency = async () => {
            console.log("=== CURRENCY DETECTION USEEFFECT TRIGGERED ===");
            console.log("autoDetect:", autoDetect);
            console.log("currencies length:", currencies?.length);
            console.log("hasDetectedCurrencyRef.current:", hasDetectedCurrencyRef.current);
            
            // Only run once and only if autoDetect is enabled
            if (!autoDetect || !currencies || currencies.length === 0 || hasDetectedCurrencyRef.current) {
                console.log("❌ Skipping currency detection:", {
                    autoDetect,
                    currenciesLength: currencies?.length,
                    alreadyDetected: hasDetectedCurrencyRef.current
                });
                return;
            }
            
            hasDetectedCurrencyRef.current = true;
            setIsDetectingCurrency(true);
            console.log("🚀 Starting currency detection...");
            console.log("Currently loaded currencies:", currencies);
            try {
                // First check for cached preference
                const cachedCurrencyId = getCachedCurrencyPreference();
                console.log(`📦 Checking cached currency ID: ${cachedCurrencyId}`);
                
                if (cachedCurrencyId) {
                    // Check if cached currency is in already fetched currencies
                    let cachedCurrency = currencies.find((c: any) => c.value === cachedCurrencyId);
                    
                    if (cachedCurrency) {
                        const selectedCurrency = {
                            id: cachedCurrency.value,
                            name: cachedCurrency.label,
                            code: cachedCurrency.initial || cachedCurrency.value
                        };
                        
                        // Save to localStorage for dynamic symbol retrieval
                        try {
                            localStorage.setItem("selectedCurrencyCode", selectedCurrency.code);
                            localStorage.setItem("selectedCurrency", JSON.stringify(selectedCurrency));
                        } catch (error) {
                            console.error("Error saving cached currency to localStorage:", error);
                        }
                        
                        setInternalSelectedCurrency(selectedCurrency);
                        onCurrencySelect(selectedCurrency);
                        setIsDetectingCurrency(false);
                        console.log("✅ Using cached currency from already fetched:", selectedCurrency);
                        return;
                    } else {
                        // Cached currency NOT in already fetched - fetch it by ID from backend
                        console.log(`❌ Cached currency ID ${cachedCurrencyId} not in already fetched. Fetching by ID from backend...`);
                        
                        // Fetch the specific currency by ID
                        const fetchResult = await dispatch(getCurrencyById({
                            id: cachedCurrencyId,
                            isShowLoader: false,
                        }));
                        
                        if (getCurrencyById.fulfilled.match(fetchResult)) {
                            const fetchedCurrency = fetchResult.payload;
                            console.log("✅ Cached currency fetched from backend:", fetchedCurrency);
                            
                            if (fetchedCurrency) {
                                // Transform to Redux structure
                                cachedCurrency = {
                                    value: fetchedCurrency.id,
                                    label: fetchedCurrency.name,
                                    initial: fetchedCurrency.initial
                                };
                                
                                const selectedCurrency = {
                                    id: cachedCurrency.value,
                                    name: cachedCurrency.label,
                                    code: cachedCurrency.initial
                                };
                                
                                // Save to localStorage for dynamic symbol retrieval
                                try {
                                    localStorage.setItem("selectedCurrencyCode", selectedCurrency.code);
                                    localStorage.setItem("selectedCurrency", JSON.stringify(selectedCurrency));
                                } catch (error) {
                                    console.error("Error saving backend-fetched cached currency to localStorage:", error);
                                }
                                
                                setInternalSelectedCurrency(selectedCurrency);
                                onCurrencySelect(selectedCurrency);
                                setIsDetectingCurrency(false);
                                console.log("✅ Using cached currency fetched from backend:", selectedCurrency);
                                return;
                            } else {
                                console.log(`⚠️ Cached currency ${cachedCurrencyId} not found in backend. Continuing to detection...`);
                            }
                        } else {
                            console.log(`⚠️ Failed to fetch cached currency ${cachedCurrencyId}. Continuing to detection...`);
                        }
                    }
                }

                // Use backend location detection - it returns currencyId directly!
                const result = await dispatch(detectUserLocation({ isShowLoader: false }));
                
                if (detectUserLocation.fulfilled.match(result)) {
                    const locationData = result.payload;
                    console.log("🌍 Location detection response:", locationData);
                    
                    const detectedCurrencyId = locationData.currencyId;
                    const currencyCode = locationData.currencyCode;
                    
                    let detectedCurrency = null;
                    
                    // Step 1: Check if the detected currency (by ID) exists in ALREADY FETCHED currencies
                    console.log("=== STEP 1: Checking in already fetched currencies ===");
                    console.log("Looking for currency ID:", detectedCurrencyId);
                    console.log("Currently loaded currencies IDs:", currencies.map((c: any) => ({ id: c.value, code: c.initial, name: c.label })));
                    
                    if (detectedCurrencyId) {
                        detectedCurrency = currencies.find((currency: any) => 
                            currency.value === detectedCurrencyId
                        );
                        
                        console.log("Search result in already fetched:", detectedCurrency);
                        
                        if (detectedCurrency) {
                            console.log("✅ Currency found in already fetched currencies by ID:", detectedCurrency);
                        } else {
                            console.log(`❌ Currency ID ${detectedCurrencyId} not found in loaded currencies.`);
                            console.log("=== STEP 2: Fetching specific currency by ID from backend ===");
                            console.log(`Fetching currency ID: ${detectedCurrencyId}`);
                            
                            // Step 2: Currency not in loaded list - fetch by ID and append
                            const fetchResult = await dispatch(getCurrencyById({
                                id: detectedCurrencyId,
                                isShowLoader: false,
                            }));
                            
                            if (getCurrencyById.fulfilled.match(fetchResult)) {
                                console.log("✅ Currency fetched successfully from backend");
                                console.log("Fetched currency payload:", fetchResult.payload);
                                
                                const fetchedCurrency = fetchResult.payload;
                                
                                if (fetchedCurrency && fetchedCurrency.id === detectedCurrencyId) {
                                    console.log("✅ Currency matches detected ID:", fetchedCurrency);
                                    
                                    // Transform backend structure to Redux structure
                                    detectedCurrency = {
                                        value: fetchedCurrency.id,
                                        label: fetchedCurrency.name,
                                        initial: fetchedCurrency.initial
                                    };
                                    
                                    console.log("✅ Transformed currency:", detectedCurrency);
                                } else {
                                    console.log("❌ Fetched currency doesn't match detected ID");
                                    console.log("Expected ID:", detectedCurrencyId);
                                    console.log("Got:", fetchedCurrency);
                                }
                            } else {
                                console.log("❌ Failed to fetch currency by ID");
                                console.log("Fetch result:", fetchResult);
                            }
                        }
                    }
                    
                    // Step 3: Fallback to EUR (base currency) if nothing found
                    console.log("=== STEP 3: Checking if fallback needed ===");
                    console.log("detectedCurrency:", detectedCurrency);
                    
                    if (!detectedCurrency) {
                        console.log("⚠️ No currency detected, falling back to EUR (base currency)...");
                        
                        // Try to find EUR in already fetched
                        detectedCurrency = currencies.find((currency: any) => 
                            currency.initial?.toUpperCase() === "EUR"
                        );
                        
                        // If EUR not in loaded currencies, search for it
                        if (!detectedCurrency) {
                            const eurSearchResult = await dispatch(getAllCurrencies({
                                page: 1,
                                limit: 5,
                                search: "EUR",
                                isShowLoader: false,
                                append: true,
                            }));
                            
                            if (getAllCurrencies.fulfilled.match(eurSearchResult)) {
                                const rawData = eurSearchResult.payload?.data || eurSearchResult.payload || [];
                                const eurFound = rawData.find((currency: any) => 
                                    currency.initial?.toUpperCase() === "EUR"
                                );
                                
                                if (eurFound) {
                                    detectedCurrency = {
                                        value: eurFound.id,
                                        label: eurFound.name,
                                        initial: eurFound.initial
                                    };
                                    console.log("✅ Fallback to EUR:", detectedCurrency);
                                }
                            }
                        } else {
                            console.log("✅ EUR found in already fetched currencies");
                        }
                    }
                    
                    // Step 4: Select the detected currency
                    console.log("=== STEP 4: Final currency selection ===");
                    console.log("Final detectedCurrency:", detectedCurrency);
                    
                    if (detectedCurrency) {
                        const selectedCurrency = {
                            id: detectedCurrency.value,
                            name: detectedCurrency.label,
                            code: detectedCurrency.initial
                        };
                        
                        console.log("🎯 About to select currency:", selectedCurrency);
                        
                        // Save to localStorage for dynamic symbol retrieval
                        try {
                            localStorage.setItem("selectedCurrencyCode", selectedCurrency.code);
                            localStorage.setItem("selectedCurrency", JSON.stringify(selectedCurrency));
                            console.log("💾 Saved detected currency to localStorage");
                        } catch (error) {
                            console.error("Error saving detected currency to localStorage:", error);
                        }
                        
                        console.log("Calling setInternalSelectedCurrency...");
                        setInternalSelectedCurrency(selectedCurrency);
                        console.log("Calling onCurrencySelect...");
                        onCurrencySelect(selectedCurrency);
                        console.log("Calling setCachedCurrencyPreference...");
                        setCachedCurrencyPreference(selectedCurrency.id);
                        console.log("✅ Currency selection complete!");
                    } else {
                        console.error("❌ Failed to detect any currency (including USD fallback)");
                        console.error("This should never happen - check backend response and database");
                    }
                }
            } catch (error) {
                console.error("❌❌❌ ERROR detecting currency:", error);
                console.error("Error stack:", error);
            } finally {
                console.log("=== Currency detection finished ===");
                setIsDetectingCurrency(false);
            }
        };
        
        console.log(">>> Calling detectAndSelectCurrency function...");
        detectAndSelectCurrency();
    }, [autoDetect, currencies.length, dispatch, onCurrencySelect]);

    // Sync with external prop
    useEffect(() => {
        console.log("=== Syncing with external selectedCurrency prop ===");
        console.log("External selectedCurrency:", selectedCurrency);
        console.log("Current internalSelectedCurrency:", internalSelectedCurrency);
        
        if (selectedCurrency !== undefined) {
            setInternalSelectedCurrency(selectedCurrency);
            console.log("✅ Internal state synced with external prop");
        }
    }, [selectedCurrency?.id]); // Only update when ID changes

    // Handle search with backend query and local filtering
    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // If search query is empty, don't search
        if (!searchQuery.trim()) {
            setIsSearching(false);
            return;
        }

        // Debounce search - wait 500ms after user stops typing
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                // Search backend for currencies matching the query
                await dispatch(getAllCurrencies({
                    page: 1,
                    limit: 20,
                    search: searchQuery.trim(),
                    isShowLoader: false,
                    append: true, // Append results to existing list
                }));
            } catch (error) {
                console.error("Error searching currencies:", error);
            } finally {
                setIsSearching(false);
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, dispatch]);

    // Filter currencies based on search query
    const filteredCurrencies = useMemo(() => {
        if (!currencies || currencies.length === 0) return [];
        
        if (!searchQuery.trim()) return currencies;
        
        return currencies.filter((currency: any) => 
            currency.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            currency.value?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            currency.initial?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [currencies, searchQuery]);

    // Handle currency selection
    const handleCurrencySelect = (currency: Currency) => {
        console.log("=== handleCurrencySelect called ===");
        console.log("Selected currency:", currency);
        
        setInternalSelectedCurrency(currency);
        onCurrencySelect(currency);
        setIsOpen(false);
        setSearchQuery("");
        
        // Save currency to localStorage for dynamic symbol retrieval
        try {
            // Save currency code for quick access
            localStorage.setItem("selectedCurrencyCode", currency.code);
            
            // Save full currency object as backup
            localStorage.setItem("selectedCurrency", JSON.stringify({
                id: currency.id,
                name: currency.name,
                code: currency.code,
                initial: currency.code
            }));
            
            console.log("💾 Saved currency to localStorage:", {
                code: currency.code,
                id: currency.id,
                name: currency.name
            });
        } catch (error) {
            console.error("Error saving currency to localStorage:", error);
        }
        
        // Cache the selected currency preference
        if (autoDetect) {
            console.log("💾 Caching currency ID:", currency.id);
            setCachedCurrencyPreference(currency.id);
        }
        
        console.log("✅ handleCurrencySelect completed");
    };

    // Handle clear selection
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setInternalSelectedCurrency(null);
        onCurrencySelect(null);
        
        // Clear currency from localStorage
        try {
            localStorage.removeItem("selectedCurrencyCode");
            localStorage.removeItem("selectedCurrency");
            console.log("🗑️ Cleared currency from localStorage");
        } catch (error) {
            console.error("Error clearing currency from localStorage:", error);
        }
    };

    // Calculate dropdown position based on available space
    useEffect(() => {
        if (isOpen && dropdownRef.current && placement === "auto") {
            const rect = dropdownRef.current.getBoundingClientRect();
            const dropdownHeight = 320; // max-h-80 (approximately 320px)
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If not enough space below but enough space above, open upward
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownPosition("top");
            } else {
                setDropdownPosition("bottom");
            }
        } else if (placement === "top") {
            setDropdownPosition("top");
        } else {
            setDropdownPosition("bottom");
        }
    }, [isOpen, placement]);

    // Handle dropdown toggle
    const toggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={toggleDropdown}
                disabled={disabled || isDetectingCurrency}
                className={`
					w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px]
					${disabled || isDetectingCurrency
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }
					transition-colors
				`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isDetectingCurrency ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                            <span className="text-gray-500">Detecting currency...</span>
                        </>
                    ) : internalSelectedCurrency ? (
                        <>
                            <span className="text-gray-500 flex-shrink-0 font-medium">
                                {getCurrencySymbol(internalSelectedCurrency.code)}
                            </span>
                            <span className="truncate">{internalSelectedCurrency.name}</span>
                            <span className="text-gray-400 text-sm">({internalSelectedCurrency.code})</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {internalSelectedCurrency && !isDetectingCurrency && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-3 w-3 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className={`absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden ${
                    dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
                }`}>
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search currencies..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Currencies List */}
                    <div className="max-h-60 overflow-y-auto">
                        {(loading && currencies.length === 0) || isSearching ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-500">
                                    {isSearching ? "Searching currencies..." : "Loading currencies..."}
                                </span>
                            </div>
                        ) : filteredCurrencies.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <DollarSign className="h-8 w-8 text-gray-300 mb-2" />
                                <p>No currencies found</p>
                                {searchQuery && (
                                    <p className="text-sm text-gray-400">Try a different search term</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {filteredCurrencies.map((currency: any) => (
                                    <button
                                        key={currency.value}
                                        type="button"
                                        onClick={() => handleCurrencySelect({
                                            id: currency.value,
                                            name: currency.label,
                                            code: currency.initial || currency.value
                                        })}
                                        className={`
											w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50
											${internalSelectedCurrency?.id === currency.value 
                                                ? "bg-blue-50 border-l-4 border-blue-500" 
                                                : ""
                                            }
											transition-colors
										`}
                                    >
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {getCurrencySymbol(currency.initial || currency.value)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {currency.label}
                                            </p>
                                            <p className="text-sm text-gray-500 truncate">{currency.initial || currency.value}</p>
                                        </div>
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

CurrencyDropdown.displayName = "CurrencyDropdown";

export default CurrencyDropdown;
